import React from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import DebateCard from "../components/community/DebateCard";
import FeaturedChallengeCard from "../components/community/FeaturedChallengeCard";
import MatchupCard from "../components/community/MatchupCard";
import RosterCard from "../components/community/RosterCard";
import { useGetCommunityProfileQuery } from "../store/api/communityApi";
import { useSportWorkspace } from "../hooks/useSportWorkspace";
import { formatRecord, formatWinRate } from "../utils/community";

const Section = ({ title, empty, children, count }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    {count === 0 ? (
      <Typography color="text.secondary">{empty}</Typography>
    ) : (
      <Box sx={{ display: "grid", gap: 2 }}>{children}</Box>
    )}
  </Box>
);

const CommunityProfile = () => {
  const { handleOrUsername } = useParams();
  const { sport } = useSportWorkspace();
  const { data: profile, isLoading, error } = useGetCommunityProfileQuery(
    { handle: handleOrUsername, sport },
    { skip: !handleOrUsername }
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return <Alert severity="info">Creator not found.</Alert>;
  }

  const label = profile.handle ? `@${profile.handle}` : profile.username;

  return (
    <Box sx={{ py: { xs: 1, sm: 2 }, maxWidth: 880, mx: "auto" }}>
      <SeoHead
        title={`${label} — Stats.Fan Community`}
        description={`${label} on Stats.Fan Community. ${formatRecord(
          profile.record
        )} roster battle record.`}
      />
      <Typography variant="overline" color="text.secondary">
        Creator
      </Typography>
      <Typography variant="h3" fontWeight={800} sx={{ mt: 0.5 }}>
        {label}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
        Roster battles {formatRecord(profile.record)} · {formatWinRate(profile.record)}{" "}
        community win rate · {profile.stats.publishedRosters} published rosters ·{" "}
        {profile.stats.debatesCreated} debates · {profile.stats.challengeCompletions}{" "}
        challenge completions
      </Typography>

      <Section title="Rosters" empty="No published rosters yet." count={profile.rosters.length}>
        {profile.rosters.map((roster) => (
          <RosterCard key={roster.id} roster={roster} sport={sport} />
        ))}
      </Section>
      <Section title="Matchups" empty="No roster battles yet." count={profile.matchups.length}>
        {profile.matchups.map((matchup) => (
          <MatchupCard key={matchup.id} matchup={matchup} sport={sport} />
        ))}
      </Section>
      <Section
        title="Challenges"
        empty="No challenges created yet."
        count={profile.challenges.length}
      >
        {profile.challenges.map((challenge) => (
          <FeaturedChallengeCard key={challenge.id} challenge={challenge} sport={sport} />
        ))}
      </Section>
      <Section title="Debates" empty="No debates created yet." count={profile.debates.length}>
        {profile.debates.map((debate) => (
          <DebateCard key={debate.id} debate={debate} sport={sport} />
        ))}
      </Section>
    </Box>
  );
};

export default CommunityProfile;
